// admin-analytics-expansion P3b (design D6/D7, spec #2559 ticket-detail) —
// TicketDetailPage
//
// Read-only dispute view aggregating the full single-ticket record from
// getTicketDetail(id): vehicle/visitor info, full lifecycle timeline, payment
// breakdown (incl. delay-recharge settlement), membership coverage, SMS
// messages, and a photos/incident-photos gallery (signed GET urls, D8).
//
// PLUS a separate audit-timeline section fed by getTicketHistory(id) (D7 —
// independent request, NOT part of the detail contract). The audit table's
// rendering shares the ACTION_LABELS map + resolveActor helper with
// TicketHistoryPage via '../../ticket-history/ui/audit-action-labels' (P3b
// review-fix — a direct page-to-page import was replaced with a shared
// module, which also picked up the missing 'ticket.reopened' label).
//
// No mutation controls exist anywhere on this page (spec: read-only enforcement).

import { ApiError, getTicketDetail, getTicketHistory } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type {
  AuditLogEntryResponse,
  TicketDetailPayment,
  TicketDetailResponse,
} from '@jq4race/shared';
import { DEFAULT_CURRENCY, paymentDisplayState } from '@jq4race/shared';
import {
  Badge,
  type BadgeProps,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  formatCents,
  Skeleton,
} from '@jq4race/ui';
import { useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { formatTimestamp } from '../../../../shared/format/format-timestamp';
import { useLocationTimeZones } from '../../../../shared/hooks/useLocationTimeZones';
import { enumLabel } from '../../../../shared/i18n/enum-label';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { useActionLabel, useResolveActor } from '../../../ticket-history/ui/audit-action-labels';

const CURRENCY_FALLBACK = DEFAULT_CURRENCY;

const PAYMENT_STATE_LABEL_KEY = {
  paid: 'paymentStatus.paid',
  due: 'paymentStatus.unpaid',
  no_charge: 'paymentStatus.noCharge',
} as const;

const STATUS_TONE: Record<TicketDetailResponse['status'], BadgeProps['variant']> = {
  parked: 'info',
  requested: 'warning',
  in_transit: 'accent',
  delivered: 'success',
  cancelled: 'danger',
};

const STATUS_LABEL_KEY: Record<TicketDetailResponse['status'], string> = {
  parked: 'status.parked',
  requested: 'status.requested',
  in_transit: 'status.inTransit',
  delivered: 'status.delivered',
  cancelled: 'status.cancelled',
};

const SMS_STATUS_TONE: Record<'queued' | 'sent' | 'failed', BadgeProps['variant']> = {
  queued: 'warning',
  sent: 'success',
  failed: 'danger',
};

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function TicketDetailPage() {
  const { t, i18n } = useTranslation('tickets');
  const resolveActionLabel = useActionLabel();
  const resolveActor = useResolveActor();
  const { ticketId } = useParams({ strict: false });

  const [detail, setDetail] = useState<TicketDetailResponse | null>(null);
  // Every timestamp on this page is shown in the ticket's own location clock.
  const { timeZoneFor } = useLocationTimeZones();
  const timeZone = timeZoneFor(detail?.locationId);
  const [detailLoading, setDetailLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [historyItems, setHistoryItems] = useState<AuditLogEntryResponse[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const loadDetail = useCallback(async () => {
    if (!ticketId) return;
    setDetailLoading(true);
    setDetailError(null);
    setNotFound(false);
    try {
      const result = await getTicketDetail(ticketId);
      setDetail(result);
    } catch (e) {
      // 403 (e.g. supervisor requesting a ticket at an unassigned location)
      // MUST render the same friendly no-access state as 404 — never leak the
      // backend's internal authorization wording ("Location not assigned to
      // this supervisor") to the admin (P3b review-fix).
      if (e instanceof ApiError && (e.status === 404 || e.status === 403)) {
        setNotFound(true);
      } else {
        setDetailError(translateApiError(e, t, { fallback: t('errors.detailLoadFailed') }));
      }
    } finally {
      setDetailLoading(false);
    }
  }, [ticketId, t]);

  // D7: independent request — the detail endpoint does NOT embed the audit
  // timeline. A failure here must not block the detail sections above.
  const loadHistory = useCallback(async () => {
    if (!ticketId) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const result = await getTicketHistory(ticketId);
      setHistoryItems(result.items);
    } catch (e) {
      setHistoryError(translateApiError(e, t, { fallback: t('errors.historyLoadFailed') }));
    } finally {
      setHistoryLoading(false);
    }
  }, [ticketId, t]);

  useEffect(() => {
    void loadDetail();
    void loadHistory();
  }, [loadDetail, loadHistory]);

  if (detailLoading) {
    return (
      <div className="space-y-8">
        <PageHeader eyebrow={t('page.eyebrow')} title={t('page.detailTitle')} />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="space-y-8">
        <PageHeader eyebrow={t('page.eyebrow')} title={t('page.detailTitle')} />
        <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
          <p className="text-[14px] font-medium text-jq-charcoal">{t('notFound')}</p>
        </div>
      </div>
    );
  }

  if (detailError || !detail) {
    return (
      <div className="space-y-8">
        <PageHeader eyebrow={t('page.eyebrow')} title={t('page.detailTitle')} />
        <div
          role="alert"
          className="rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
        >
          {detailError ?? t('errors.detailLoadFailed')}
        </div>
      </div>
    );
  }

  const visitorName = [detail.firstName, detail.lastName].filter(Boolean).join(' ') || '—';

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.detailNumberTitle', { number: detail.ticketNumber })}
        description={t('page.detailPlateDescription', { plate: detail.plate })}
        meta={
          <Badge variant={STATUS_TONE[detail.status]}>{t(STATUS_LABEL_KEY[detail.status])}</Badge>
        }
      />

      <SectionCard title={t('sections.vehicleVisitor')}>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <InfoField label={t('fields.plate')} value={detail.plate} />
          <InfoField label={t('fields.qrTag')} value={detail.qrTag ?? '—'} />
          <InfoField label={t('fields.ticketType')} value={detail.ticketType.toUpperCase()} />
          <InfoField label={t('fields.visitor')} value={visitorName} />
          <InfoField label={t('fields.phone')} value={detail.phone ?? '—'} />
          <InfoField label={t('fields.email')} value={detail.visitorEmail ?? '—'} />
          <InfoField label={t('fields.roomNumber')} value={detail.roomNumber ?? '—'} />
          <InfoField
            label={t('fields.visitorType')}
            value={enumLabel(t, 'visitorType', detail.visitorType)}
          />
          <InfoField label={t('fields.parkingZone')} value={detail.parkingZone ?? '—'} />
          <InfoField label={t('fields.valetNotes')} value={detail.valetNotes ?? '—'} />
        </div>
      </SectionCard>

      <SectionCard title={t('sections.lifecycle')}>
        <ul className="space-y-2.5 text-[13px] text-jq-graphite">
          <TimelineRow
            label={t('lifecycle.checkedIn')}
            value={formatTimestamp(detail.checkedInAt, i18n.language, timeZone)}
          />
          <TimelineRow
            label={t('lifecycle.retrievalRequested')}
            value={formatTimestamp(detail.retrievalRequestedAt, i18n.language, timeZone)}
          />
          <TimelineRow
            label={t('lifecycle.claimed')}
            value={formatTimestamp(detail.claimedAt, i18n.language, timeZone)}
          />
          <TimelineRow
            label={t('lifecycle.readyForPickup')}
            value={formatTimestamp(detail.readyForPickupAt, i18n.language, timeZone)}
          />
          <TimelineRow
            label={t('lifecycle.delivered')}
            value={formatTimestamp(detail.deliveredAt, i18n.language, timeZone)}
          />
          <TimelineRow
            label={t('lifecycle.retrievalDuration')}
            value={formatDuration(detail.retrievalDurationSeconds)}
          />
        </ul>
      </SectionCard>

      <SectionCard title={t('sections.payment')}>
        <PaymentBreakdown payment={detail.payment} timeZone={timeZone} />
      </SectionCard>

      <SectionCard title={t('sections.membership')}>
        {detail.membership ? (
          <Badge variant="success">
            {t('membership.covered', {
              status: enumLabel(t, 'membershipStatus', detail.membership.status),
            })}
          </Badge>
        ) : (
          <Badge variant="outline">{t('membership.notCovered')}</Badge>
        )}
      </SectionCard>

      <SectionCard title={t('sections.sms')}>
        {detail.smsMessages.length === 0 ? (
          <p className="text-[13px] text-jq-graphite/60">{t('empty.sms')}</p>
        ) : (
          <ul className="space-y-3">
            {detail.smsMessages.map((m) => (
              <li
                key={m.id}
                className="rounded-sm border border-jq-sand/50 p-3 text-[13px] text-jq-graphite"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={SMS_STATUS_TONE[m.status]}>
                    {enumLabel(t, 'smsStatus', m.status)}
                  </Badge>
                  <span className="uppercase text-[11px] text-jq-graphite/70">{m.locale}</span>
                  <span className="font-mono text-[12px] text-jq-graphite">
                    {formatTimestamp(m.createdAt, i18n.language, timeZone)}
                  </span>
                </div>
                <p className="mt-1.5 font-mono text-[12px] text-jq-charcoal">
                  {t('sms.to', { phone: m.to })}
                </p>
                <p className="mt-1 whitespace-pre-wrap text-[13px] text-jq-charcoal">{m.body}</p>
                {m.status === 'failed' && m.errorCode && (
                  <p className="mt-1.5 text-[12px] text-jq-danger">
                    {t('sms.error', { code: m.errorCode })}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      <SectionCard title={t('sections.photos')}>
        {detail.photos.length === 0 ? (
          <p className="text-[13px] text-jq-graphite/60">{t('empty.photos')}</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {detail.photos.map((photo) => (
              <button
                key={photo.id}
                type="button"
                onClick={() => setLightboxUrl(photo.url)}
                className="group relative overflow-hidden rounded-sm border border-jq-sand/70"
              >
                <img
                  src={photo.url}
                  alt={t('photos.altVehicle', { angle: enumLabel(t, 'photoAngle', photo.angle) })}
                  className="aspect-square w-full object-cover transition group-hover:opacity-80"
                />
                <span className="absolute bottom-1 left-1 rounded-full bg-jq-ink/60 px-2 py-0.5 text-[10px] uppercase text-white">
                  {enumLabel(t, 'photoAngle', photo.angle)}
                </span>
              </button>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard title={t('sections.incidentPhotos')}>
        {detail.incidentPhotos.length === 0 ? (
          <p className="text-[13px] text-jq-graphite/60">{t('empty.incidentPhotos')}</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {detail.incidentPhotos.map((photo, index) => (
              <button
                key={photo.id}
                type="button"
                onClick={() => setLightboxUrl(photo.url)}
                className="group relative overflow-hidden rounded-sm border border-jq-sand/70"
              >
                <img
                  src={photo.url}
                  alt={t('incidentPhotos.alt', { index: index + 1 })}
                  className="aspect-square w-full object-cover transition group-hover:opacity-80"
                />
              </button>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard title={t('sections.auditTimeline')}>
        {historyError ? (
          <p className="text-[13px] text-jq-danger">{historyError}</p>
        ) : historyLoading ? (
          <p className="text-[13px] text-jq-graphite">{t('common:states.loading')}</p>
        ) : historyItems.length === 0 ? (
          <p className="text-[13px] text-jq-graphite/60">{t('empty.history')}</p>
        ) : (
          <table className="w-full text-[13px] text-jq-graphite">
            <thead>
              <tr className="border-b border-jq-sand/50 text-left text-[12px] text-jq-graphite/60">
                <th className="pb-2 pr-4 font-medium">{t('table.action')}</th>
                <th className="pb-2 pr-4 font-medium">{t('table.actor')}</th>
                <th className="pb-2 font-medium">{t('table.time')}</th>
              </tr>
            </thead>
            <tbody>
              {historyItems.map((item) => (
                <tr key={item.id} className="border-b border-jq-sand/30 last:border-0">
                  <td className="py-2 pr-4">{resolveActionLabel(item.action)}</td>
                  <td className="py-2 pr-4">{resolveActor(item)}</td>
                  <td className="py-2 font-mono text-[12px]">
                    {formatTimestamp(item.createdAt, i18n.language, timeZone)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>

      <Dialog open={lightboxUrl !== null} onOpenChange={(open) => !open && setLightboxUrl(null)}>
        <DialogContent className="max-w-2xl">
          <DialogTitle className="sr-only">{t('dialog.photoPreview')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('dialog.photoPreviewDescription')}
          </DialogDescription>
          {lightboxUrl && (
            <img src={lightboxUrl} alt={t('dialog.enlargedAlt')} className="w-full rounded-sm" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Building blocks ───────────────────────────────────────────────────────────

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
      <CardContent className="p-6 sm:p-8">
        <h2 className="mb-4 text-[15px] font-medium text-jq-charcoal">{title}</h2>
        {children}
      </CardContent>
    </Card>
  );
}

function InfoField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-widest text-jq-graphite">{label}</p>
      <p className="mt-1 text-[13px] text-jq-charcoal">{value}</p>
    </div>
  );
}

function TimelineRow({ label, value }: { label: string; value: string }) {
  return (
    <li>
      <span className="font-medium text-jq-charcoal">{label}:</span> {value}
    </li>
  );
}

function PaymentBreakdown({
  payment,
  timeZone,
}: {
  payment: TicketDetailPayment;
  timeZone: string;
}) {
  const { t, i18n } = useTranslation('tickets');
  const hasDelayRecharge =
    payment.delayRechargeIntentId !== null ||
    payment.delayRechargeAmountCents !== null ||
    payment.delayRechargePaidAt !== null;

  // QA R3-10: an unpaid ticket whose total is $0 owes nothing — "No charge", not "Unpaid".
  const paymentState =
    paymentDisplayState({
      paymentStatus: payment.status,
      paymentRequired: payment.paymentRequired,
    }) ?? 'due';

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <InfoField label={t('payment.status')} value={t(PAYMENT_STATE_LABEL_KEY[paymentState])} />
      <InfoField
        label={t('payment.method')}
        value={enumLabel(t, 'paymentMethod', payment.method)}
      />
      <InfoField
        label={t('payment.amountPaid')}
        value={
          payment.amountPaidCents !== null
            ? formatCents(payment.amountPaidCents, CURRENCY_FALLBACK)
            : '—'
        }
      />
      <InfoField
        label={t('payment.tip')}
        value={
          payment.tipAmountCents !== null
            ? formatCents(payment.tipAmountCents, CURRENCY_FALLBACK)
            : '—'
        }
      />
      <InfoField
        label={t('payment.paidAt')}
        value={formatTimestamp(payment.paidAt, i18n.language, timeZone)}
      />
      {hasDelayRecharge && (
        <>
          <InfoField
            label={t('payment.delayRecharge')}
            value={
              // A recharge was initiated (delayRechargeIntentId present) but
              // has not yet settled (delayRechargePaidAt still null) — show
              // "Pending", not a bare "—", so this is never confused with
              // "no delay recharge at all" (P3b review-fix).
              payment.delayRechargePaidAt === null
                ? t('payment.pending')
                : payment.delayRechargeAmountCents !== null
                  ? formatCents(payment.delayRechargeAmountCents, CURRENCY_FALLBACK)
                  : '—'
            }
          />
          <InfoField
            label={t('payment.delayRechargePaidAt')}
            value={formatTimestamp(payment.delayRechargePaidAt, i18n.language, timeZone)}
          />
        </>
      )}
    </div>
  );
}
